import cloneDeep from 'lodash/cloneDeep';
import { ITERATOR_NODE_TYPES } from '@/consts/node-types.consts';
import { isModelNode } from '@/components/Nodes/utils/nodeUtils';
import { isIterator } from './iterator.utils';
import type { NodeId } from 'web';
import type { BaseNodeData, Edge, ListSelectorData, MediaIteratorData, Node } from '../types/node';
import type { ModelBaseNodeData } from '@/types/nodes/model';

type IteratorNodeData = BaseNodeData & (ListSelectorData | MediaIteratorData);
export type PrecedingIteratorData = {
  iteratorNode: Node & { data: IteratorNodeData };
  inputKey: string;
  nodeId: NodeId;
};

export type PrecedingIteratorsWithNodesResult = {
  iterators: PrecedingIteratorData[];
  nodes: Node[];
};

/**
 * Finds all preceding iterator nodes that feed into the specified node.
 *
 * This function traverses the flow graph backwards from the given node to find
 * all iterator nodes that are connected to its input handles. It recursively
 * follows edges through router nodes to find the actual iterator sources.
 *
 * @param nodeId - The ID of the target node to find preceding iterators for
 * @param nodes - Array of all nodes in the flow graph
 * @param edges - Array of all edges in the flow graph
 * @returns An array of objects containing the preceding iterator node and its corresponding input key, and the given node id,
 *          or null if no preceding iterators are found or if the nodeId is invalid
 *
 * @remarks
 * - If the nodeId itself is an iterator node or a router node, it is ignored.
 * - The function handles router nodes by recursively traversing through them to find the actual source
 * - Currently supports 'muxv2' as an iterator type, with a TODO for adding other iterator types
 * - Input keys are extracted from edge target handles using the pattern '-input-{key}'
 */
export const getPrecedingIterators = (nodeId: NodeId, nodes: Node[], edges: Edge[]): PrecedingIteratorData[] | null => {
  if (!nodeId) return null;
  const node = nodes.find((node) => node.id === nodeId);

  if (!node || node.type === 'router') return null;

  const incomingEdges = edges.filter((edge) => edge.target === nodeId);
  if (incomingEdges.length === 0) return null;

  const precedingIterators: PrecedingIteratorData[] = [];
  // Repeat for all incoming edges (all input handles)
  for (const edge of incomingEdges) {
    // Recursively find preceding iterator node
    const getPrecedingNode = (edge: Edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      if (!sourceNode) return null;
      if (sourceNode.type === 'router') {
        const routerSourceEdge = edges.find((e) => e.target === sourceNode.id);
        if (!routerSourceEdge) return null;
        return getPrecedingNode(routerSourceEdge);
      }
      if (ITERATOR_NODE_TYPES.includes(sourceNode.type as (typeof ITERATOR_NODE_TYPES)[number])) {
        return sourceNode as Node & { data: IteratorNodeData };
      }
      return null;
    };
    // Get the input key from the edge
    const inputKey = edge.targetHandle?.split('-input-')?.[1];
    if (!inputKey) continue;

    const precedingNode = getPrecedingNode(edge);
    if (precedingNode && isIterator(precedingNode.data)) {
      precedingIterators.push({ iteratorNode: precedingNode, inputKey, nodeId });
    }
  }
  return precedingIterators.length > 0 ? precedingIterators : null;
};

/**
 * Iteratively find preceding iterator nodes and collect path nodes which are not model nodes.
 * @param edge - The edge to start the traversal from
 * @param nodes - The nodes in the flow graph
 * @param edges - The edges in the flow graph
 * @param modelNodesInBatch - Set of model node IDs that are part of the current batch (traversal continues through these)
 * @returns An object containing the preceding iterator nodes and the path nodes
 */
const getPrecedingNodesWithPathForIncomingEdge = (
  edge: Edge,
  nodes: Node[],
  edges: Edge[],
  modelNodesInBatch: Set<NodeId>,
): { iterators: (Node & { data: IteratorNodeData })[]; pathNodes: Node[] } => {
  const foundIterators: (Node & { data: IteratorNodeData })[] = [];
  const pathNodes: Node[] = [];
  const stack: Edge[] = [edge];

  while (stack.length > 0) {
    const currentEdge = stack.pop()!;
    const sourceNode = nodes.find((node) => node.id === currentEdge.source);

    if (!sourceNode) continue;

    // If it's a model node that's NOT in our batch, stop traversing
    // Model nodes in the batch should be traversed through to find their iterators
    if (isModelNode(sourceNode) && !modelNodesInBatch.has(sourceNode.id)) {
      continue;
    }

    pathNodes.push(sourceNode);

    // If it's an iterator, add it to results
    if (ITERATOR_NODE_TYPES.includes(sourceNode.type as (typeof ITERATOR_NODE_TYPES)[number])) {
      foundIterators.push(sourceNode as Node & { data: IteratorNodeData });
      continue;
    }

    // For any other node type, add all its incoming edges to the stack
    const sourceIncomingEdges = edges.filter((e) => e.target === sourceNode.id);
    stack.push(...sourceIncomingEdges);
  }

  return { iterators: foundIterators, pathNodes };
};

/**
 * Finds all preceding iterator nodes that feed into the specified node, along with all nodes on the path.
 *
 * This function traverses the flow graph backwards from the given node to find
 * all iterator nodes that are connected to its input handles. It recursively
 * follows edges through intermediate nodes to find the iterator sources and collects
 * all nodes encountered along the path.
 *
 * @param nodeId - The ID of the target node to find preceding iterators for
 * @param nodes - Array of all nodes in the flow graph
 * @param edges - Array of all edges in the flow graph
 * @param modelNodesInBatch - Set of model node IDs that are part of the current batch (traversal continues through these)
 * @returns An object containing:
 *          - iterators: Array of objects with the preceding iterator node, its corresponding input key, and the given node id
 *          - nodes: Array of all nodes on the path from the target node to the iterators (including both)
 *          or null if no preceding iterators are found or if the nodeId is invalid
 *
 * @remarks
 * - Traversal stops when encountering a model node that is NOT in the batch
 * - Traversal continues through model nodes that ARE in the batch to find their iterators
 */
export const getPrecedingNodesUntilIterator = (
  nodeId: NodeId,
  nodes: Node[],
  edges: Edge[],
  modelNodesInBatch: Set<NodeId> = new Set(),
): PrecedingIteratorsWithNodesResult | null => {
  if (!nodeId) return null;
  const node = nodes.find((node) => node.id === nodeId);
  if (!node || node.type === 'router') return null;

  const incomingEdges = edges.filter((edge) => edge.target === nodeId);
  if (incomingEdges.length === 0) return null;

  const precedingIterators: PrecedingIteratorData[] = [];
  const allPathNodes: Node[] = [node]; // Start with the initial target node
  const seenNodeIds = new Set<NodeId>([nodeId]); // Track seen nodes to avoid duplicates

  // Repeat for all incoming edges (all input handles)
  for (const edge of incomingEdges) {
    // Get the input key from the edge
    const inputKey = edge.targetHandle?.split('-input-')?.[1];
    if (!inputKey) continue;

    const result = getPrecedingNodesWithPathForIncomingEdge(edge, nodes, edges, modelNodesInBatch);

    // Collect path nodes, avoiding duplicates
    for (const pathNode of result.pathNodes) {
      if (!seenNodeIds.has(pathNode.id)) {
        allPathNodes.push(pathNode);
        seenNodeIds.add(pathNode.id);
      }
    }

    // Collect iterators
    for (const precedingNode of result.iterators) {
      if (isIterator(precedingNode.data)) {
        precedingIterators.push({ iteratorNode: precedingNode, inputKey, nodeId });
      }
    }
  }

  return precedingIterators.length > 0 ? { iterators: precedingIterators, nodes: allPathNodes } : null;
};

/**
 * Collects all preceding nodes and iterators for a given list of model nodes.
 *
 * @param modelNodes - Array of model nodes to collect preceding nodes and iterators for
 * @param nodes - Array of all nodes in the flow graph
 * @param edges - Array of all edges in the flow graph
 * @returns An object containing the preceding nodes and iterators
 */
export const collectPrecedingNodesAndIterators = (
  modelNodes: Node[],
  nodes: Node[],
  edges: Edge[],
): {
  precedingNodes: Node[];
  precedingIterators: Array<{
    nodeId: string;
    inputKey: string;
    iteratorNode: Node;
  }>;
} => {
  // Create a set of model node IDs in this batch for quick lookup
  // This allows traversal to continue through model nodes that are part of the batch
  const modelNodeIds = new Set<NodeId>(modelNodes.map((n) => n.id));

  return modelNodes.reduce<{
    precedingNodes: Node[];
    precedingIterators: Array<{
      nodeId: string;
      inputKey: string;
      iteratorNode: Node;
    }>;
    seenNodeIds: Set<string>;
  }>(
    (acc, modelNode) => {
      const result = getPrecedingNodesUntilIterator(modelNode.id, nodes, edges, modelNodeIds);

      // Collect iterators
      if (result?.iterators) {
        acc.precedingIterators.push(...result.iterators.filter((item) => item !== null));
      }

      // Collect and deduplicate nodes
      if (result?.nodes) {
        for (const node of result.nodes) {
          if (!acc.seenNodeIds.has(node.id)) {
            acc.precedingNodes.push(node);
            acc.seenNodeIds.add(node.id);
          }
        }
      } else {
        // If no preceding iterators were found, still include the model node itself
        if (!acc.seenNodeIds.has(modelNode.id)) {
          acc.precedingNodes.push(modelNode);
          acc.seenNodeIds.add(modelNode.id);
        }
      }

      return acc;
    },
    { precedingNodes: [], precedingIterators: [], seenNodeIds: new Set<string>() },
  );
};

/**
 * Updates the input data for model nodes before running.
 * This function clones the provided nodes and calls the update function on each model node.
 *
 * @param nodes - Array of nodes to update
 * @param updateInputBeforeRun - Function that updates a model node's input before running
 * @returns Cloned and updated array of nodes
 */
const updateModelNodesInputs = (
  nodes: Node[],
  updateInputBeforeRun: (nodeId: string, nodeData: ModelBaseNodeData) => void,
): Node[] => {
  const nodesClone = cloneDeep(nodes);
  return nodesClone.map((node) => {
    if (isModelNode(node)) {
      updateInputBeforeRun(node.id, node.data as ModelBaseNodeData);
    }
    return node;
  });
};

/**
 * A new implementation of the batch preparation for the run.
 * If there are iterators, it collects all nodes and edges connecting iterators with the model nodes and add them to the selected nodes.
 * If there are no iterators, it just passes the selected nodes and their related edges.
 *
 * @param nodesToRun - Array of nodes selected to run
 * @param nodes - Array of all nodes in the flow graph
 * @param edges - Array of all edges in the flow graph
 * @param updateInputBeforeRun - Function that updates a model node's input before running
 * @returns An object containing the nodes to run, edges, and whether there are iterators
 */
export const prepareBatchDataForRun = (
  nodesToRun: Node[],
  nodes: Node[],
  edges: Edge[],
  updateInputBeforeRun: (nodeId: string, nodeData: ModelBaseNodeData) => void,
): { nodes: Node[]; edges: Edge[]; hasIterators: boolean } => {
  const selectedModelNodes = nodesToRun.filter(isModelNode);

  // If there are iterators, we need to pass the model nodes and the preceding nodes and edges to the server,
  // otherwise we just pass the selected model nodes
  const { precedingNodes, precedingIterators } = collectPrecedingNodesAndIterators(selectedModelNodes, nodes, edges);

  const hasIterators = precedingIterators.length > 0;
  const precedingNodeIds = new Set(precedingNodes.map((node) => node.id));
  // Add any selected nodes that weren't included in preceding nodes
  const additionalSelectedNodes = nodesToRun.filter((node) => !precedingNodeIds.has(node.id));
  const relevantNodesToRun = hasIterators ? [...precedingNodes, ...additionalSelectedNodes] : nodesToRun;
  // Update the legacy input for model nodes on the new architecture to avoid changes to the server
  const relevantNodesUpdated = updateModelNodesInputs(relevantNodesToRun, updateInputBeforeRun);

  // Include edges related to all relevant nodes
  const allRelevantNodeIds = new Set(relevantNodesUpdated.map((node) => node.id));
  const relevantEdges = edges.filter(
    (edge) => allRelevantNodeIds.has(edge.source) || allRelevantNodeIds.has(edge.target),
  );
  return { nodes: relevantNodesUpdated, edges: relevantEdges, hasIterators };
};
